/* eslint react-hooks/exhaustive-deps: 0 */
import { useEffect, useState, useContext, forwardRef } from 'react';
import {
  TextField,
  Grid,
  Button,
  Select,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Alert,
} from '@mui/material';

import { AuthContext } from 'components';

const parseLocaleNumber = (stringNumber, locale = 'en-US') => {
  try {
    var thousandSeparator = Intl.NumberFormat(locale).format(11111).replace(/\p{Number}/gu, '');
    var decimalSeparator = Intl.NumberFormat(locale).format(1.1).replace(/\p{Number}/gu, '');
  
    return parseFloat(stringNumber
        .replace(new RegExp('\\' + thousandSeparator, 'g'), '')
        .replace(new RegExp('\\' + decimalSeparator), '.')
    );
  } catch {
    return stringNumber
  }
}

const formatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const parseAndFormat = (value) => {
  let formatted = formatter.format(value)
  if (isNaN(formatted)) {
    formatted = formatter.format(parseLocaleNumber(value))
  }
  return formatted
}

const validateInput = (value) => {
  return /^[0-9\.]*$/.test(value)
}


export const PriceInfo = (props) => {
  const { onChange, priceInfo } = props // brand id
  const [ price, setPrice ] = useState(priceInfo?.price ? formatter.format(priceInfo?.price) : '')
  const [ retailPrice, setRetailPrice ] = useState(priceInfo?.retailPrice ? formatter.format(priceInfo?.retailPrice) : '')
  const [ markup, setMarkup ] = useState(priceInfo?.markup ? formatter.format(priceInfo?.markup) : '')
  const [ quantity, setQuantity ] = useState(priceInfo?.quantity || '')
  const { exchangeRates, storeCurrency } = useContext(AuthContext);
  const [ costCurrency, setCostCurrency ] = useState(priceInfo?.currency || storeCurrency)
  const [ costCurrencyDialogOpen, setCostCurrencyDialogOpen ] = useState(false)
  const [ initialRenderComplete, setInitialRenderComplete ] = useState(false)
  const [ error, setError ] = useState(false)
  let retailCurrency = priceInfo?.retailPriceCurrency || storeCurrency
  const [ retailCurrencyToCostCurrencyRate, setRetailCurrencyToCostCurrencyRate ] = useState(exchangeRates[retailCurrency].rate / exchangeRates[costCurrency].rate)

  useEffect(() => {
    if (price && retailPrice && !markup) {
      let newMarkup = retailPrice / price / retailCurrencyToCostCurrencyRate
      setMarkup(formatter.format(newMarkup))
    }
  }, [])

  useEffect(() => {
    setRetailCurrencyToCostCurrencyRate(exchangeRates[retailCurrency].rate / exchangeRates[costCurrency].rate)
  }, [costCurrency])

  useEffect(() => {
    if (price && markup && initialRenderComplete) {
      let newRetailPrice = price * retailCurrencyToCostCurrencyRate * markup
      setRetailPrice(formatter.format(newRetailPrice))
    }
    handleChange()
  }, [retailCurrencyToCostCurrencyRate])

  const handleCostCurrencyDialogOpen = () => setCostCurrencyDialogOpen(true);
  const handleCostCurrencyDialogClose = () => setCostCurrencyDialogOpen(false);

  const handleChange = () => {
    if (error) return
    let newPriceInfo = priceInfo || {}
    if (!isNaN(parseLocaleNumber(price))) { newPriceInfo.price = parseLocaleNumber(price) }
    if (!isNaN(parseLocaleNumber(retailPrice))) { newPriceInfo.retailPrice = parseLocaleNumber(retailPrice) }
    if (!isNaN(parseLocaleNumber(markup))) { newPriceInfo.markup = parseLocaleNumber(markup) }
    if (quantity) {
      newPriceInfo.quantity = parseInt(quantity)
    }

    newPriceInfo.currency = costCurrency
    newPriceInfo.retailPriceCurrency = retailCurrency

    if (onChange) {
      onChange(newPriceInfo)
      // onChange({
      //   ...priceInfo,
      //   currency: costCurrency,
      //   price: parseFloat(price),
      //   retailPrice: parseFloat(retailPrice),
      //   markup: parseFloat(markup),
      //   quantity: quantity ? parseInt(quantity) : 0
      // })
    }
  }

  useEffect(() => {
    handleChange()
  }, [quantity, retailPrice, price, markup])

  useEffect(() => {
    setInitialRenderComplete(true)
  }, [])

  return (
    <Grid container rowSpacing={3} spacing={1}>
      <Grid item xs={2}>
        <TextField
          InputLabelProps={{ shrink: true }}
          variant='standard'
          labels={`Price (${costCurrency})`}
          label={<>Price (<a style={{textDecoration: 'underline', cursor: 'pointer'}} onClick={handleCostCurrencyDialogOpen}>{costCurrency}</a>)</>}
          value={price}
          onFocus={(e) => {
            let value = e.target.value
            setPrice(value.replaceAll(',', ''))
          }}
          onChange={(e) => {
            let newPrice = e.target.value.replaceAll(',', '.')
            setPrice(newPrice)

            if (!validateInput(newPrice)) {
              setError('Please enter a numeric value for price.')
              return
            }

            setError(false)
            // console.log('onChange newPrice', newPrice)
            if (markup && markup !== 0) {
              let newRetailPrice = parseLocaleNumber(newPrice) * retailCurrencyToCostCurrencyRate * markup
              if (!isNaN(newRetailPrice) && newRetailPrice !== 0) {
                setRetailPrice(formatter.format(newRetailPrice))
              }
            }
          }}
          onBlur={(e) => {
            if (error) return
            if (e.target.value) {
              let newPrice = parseAndFormat(e.target.value)
              setPrice(newPrice)
            }
          }}
        />
      </Grid>

      <Grid item xs={2.5}>
        <TextField
          InputLabelProps={{ shrink: true }}
          variant='standard'
          label={`Retail Price (${retailCurrency})`}
          value={retailPrice}
          onFocus={(e) => {
            let value = e.target.value
            setRetailPrice(value.replaceAll(',', ''))
          }}
          onChange={(e) => { 
            let newRetailPrice = e.target.value.replaceAll(',', '.')
            // console.log('onChange newRetailPrice', newRetailPrice)
            setRetailPrice(newRetailPrice)

            if (!validateInput(newRetailPrice)) {
              setError('Please enter a numeric value for retail price.')
              return
            }

            setError(false)
            if (price && price !== 0) {
              let newMarkup = parseLocaleNumber(newRetailPrice) / parseLocaleNumber(price) / retailCurrencyToCostCurrencyRate
              if (!isNaN(newMarkup) && newMarkup !== 0) {
                setMarkup(formatter.format(newMarkup))
              }
            }
          }}
          onBlur={(e) => { 
            if (error) return
            if (e.target.value) {
              let newRetailPrice = parseAndFormat(e.target.value)
              // console.log('onBlur newRetailPrice', newRetailPrice)
              setRetailPrice(newRetailPrice)
            }
          }}
        />
      </Grid>

      <Grid item xs={2}>
        <TextField
          InputLabelProps={{ shrink: true }}
          variant='standard'
          label="Markup"
          value={markup}
          onFocus={(e) => {
            let value = e.target.value
            setMarkup(value.replaceAll(',', ''))
          }}
          onChange={(e) => {
            let newMarkup = e.target.value.replaceAll(',', '.')
            // console.log('onChange newMarkup', newMarkup)
            setMarkup(newMarkup)

            if (!validateInput(newMarkup)) {
              setError('Please enter a numeric value for markup.')
              return
            }
            setError(false)

            if (price && newMarkup !== 0) {
              let newRetailPrice = parseLocaleNumber(newMarkup) * parseLocaleNumber(price) * retailCurrencyToCostCurrencyRate
              if (!isNaN(newRetailPrice) && newRetailPrice !== 0) {
                setRetailPrice(formatter.format(newRetailPrice))
              }
            }
          }}
          onBlur={(e) => {
            if (error) return
            if (e.target.value) {
              let newMarkup = parseAndFormat(e.target.value)
              // console.log('onBlur newMarkup', newMarkup)
              setMarkup(newMarkup)
            }
          }}
        />
      </Grid>

      <Grid item xs={2}>
        <TextField
          InputLabelProps={{ shrink: true }}
          variant='standard'
          label="Quantity"
          value={quantity}
          onChange={(e) => { 
            let quantity = e.target.value
            setQuantity(quantity)
            // if (!validateInput(quantity)) {
            if (!/^[0-9]*$/.test(quantity)) {
              setError('Please enter a whole number for quantity.')
              return
            }
            setError(false)
          }}
        />
      </Grid>

      {error ? (
        <Alert severity="error">{error}</Alert>
      ) : null}

      <Dialog disableEscapeKeyDown open={costCurrencyDialogOpen} onClose={handleCostCurrencyDialogClose}>
        <DialogTitle>Change purchase currency</DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ display: 'flex', flexWrap: 'wrap' }}>
            <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
              <InputLabel id="cost-currency-label">Currency</InputLabel>
              <Select
                labelId="cost-currency-label"
                id="cost-currency"
                value={costCurrency}
                label="Currency"
                onChange={(e) => {
                  setCostCurrency(e.target.value)
                  handleCostCurrencyDialogClose()
                }}
              >
                {Object.keys(exchangeRates).map(e => e.length === 3 && <MenuItem key={e} value={e}>{e}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCostCurrencyDialogClose}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Grid>
  )
}